/* This file is taken from the open source project 'ra-data-drf'. The MIT license can
be found here: https://github.com/synaptic-cl/ra-data-drf/blob/develop/LICENSE  */

import * as queryString from 'query-string'
import {
  CREATE,
  DELETE,
  DELETE_MANY,
  fetchUtils,
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  Identifier,
  UPDATE,
  UPDATE_MANY,
} from 'react-admin'

import { detectEnvironment, Environment } from '../config/config'
import { getCsrfCookieToken } from '../helpers/authHelper'

const env = detectEnvironment()

const fetchJson = (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' })
  }
  let token = getCsrfCookieToken()
  options.headers.set('x-csrftoken', token)

  if (env === Environment.PREVIEW) {
    options.headers.set('X-From', 'netlify')
    options.mode = 'cors'
    options.credentials = 'include'

    if (!url.startsWith('http')) {
      url = `https://console.cloudtrucks-staging.com${url}`
    }
  }

  return fetchUtils.fetchJson(url, options)
}

/**
 * Converts a reference to an endpoint (e.g. from ReferenceManyField) to a nested resource.
 * By default, React Admin doesn't support nested resources the suggested solution
 * is to customize the data provider.
 * see https://stackoverflow.com/a/54292675/15638836
 */
const convertReferenceToNested = (type: string, resource: string, params: any) => {
  resource = `${params.target}/${params.id}/${resource}`
  return { type, resource, params }
}

// these resources are treated as nested
const NESTED_RESOURCES = ['flags']

/**
 * Maps react-admin queries to the default format of Django REST Framework
 */
const drfProvider = (apiUrl: string, httpClient = fetchJson) => {
  /**
   * @param {String} type React-admin request type, e.g. 'GET_LIST'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params Request parameters. Depends on the request type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertDataRequestToHttp = (type: string, resource: string, params: any) => {
    let url = ''
    let options: any = {}

    switch (type) {
      case CREATE:
        url = `${apiUrl}/${resource}/`
        options.method = 'POST'
        options.body = JSON.stringify(params.data)
        break
      case GET_ONE:
        url = `${apiUrl}/${resource}/${params.id}/`
        break
      case GET_LIST: {
        const { page, perPage } = params.pagination
        const { field, order } = params.sort
        const { filter } = params
        const query = {
          page,
          page_size: perPage,
          ordering: `${order === 'ASC' ? '' : '-'}${field}`,
          ...filter,
        }
        url = `${apiUrl}/${resource}/?${queryString.stringify(query)}`
        break
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination
        const { field, order } = params.sort
        const { filter, target, id } = params
        const query = {
          page,
          page_size: perPage,
          ordering: `${order === 'ASC' ? '' : '-'}${field}`,
          ...filter,
          [target]: id,
        }
        url = `${apiUrl}/${resource}/?${queryString.stringify(query)}`
        break
      }
      case UPDATE:
        url = `${apiUrl}/${resource}/${params.id}/`
        options.method = 'PUT'
        options.body = JSON.stringify(params.data)
        break
      case DELETE:
        url = `${apiUrl}/${resource}/${params.id}/`
        options.method = 'DELETE'
        break
      default:
        throw new Error(`Unsupported Data Provider request type ${type}`)
    }

    return { url, options }
  }

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type React-admin request type, e.g. 'GET_LIST'
   * @param {Object} params Request parameters. Depends on the request type
   * @returns {Object} Data response
   */
  const convertHttpResponse = (response: any, type: string, params: any) => {
    const { headers, json } = response

    switch (type) {
      case GET_LIST:
      case GET_MANY_REFERENCE:
        if ('count' in json) {
          return { data: json.results, total: json.count }
        } else if (headers.has('content-range')) {
          return {
            data: json,
            total: parseInt(headers.get('content-range').split('/').pop(), 10),
          }
        } else if ('detail' in json && json.detail === 'Invalid page.') {
          return { data: [], total: 0 }
        } else {
          throw new Error(
            'The total number of results is unknown. The DRF data provider ' +
              'expects responses for lists of resources to contain this ' +
              "information to build the pagination. If you're not using the " +
              'default PageNumberPagination class, please include this ' +
              'information using the Content-Range header OR a "count" key ' +
              'inside the response.'
          )
        }
      case CREATE:
        return { data: { ...params.data, id: json.id } }
      case DELETE:
        return { data: params.previousData }
      default:
        return { data: json }
    }
  }

  /**
   * @param {String} type React-admin request type, e.g. 'GET_LIST'
   * @param {string} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a data response
   */
  return (type: string, resource: string, params: any) => {
    /**
     * Split GET_MANY, UPDATE_MANY and DELETE_MANY requests into multiple promises,
     * since they're not supported by default.
     */

    if (NESTED_RESOURCES.includes(resource)) {
      ;({ type, resource, params } = convertReferenceToNested(type, resource, params))
    }

    switch (type) {
      case GET_MANY:
        return Promise.all(
          params.ids.map((id: Identifier) =>
            httpClient(`${apiUrl}/${resource}/${id}/`, {
              method: 'GET',
            })
          )
        ).then((responses) => ({
          data: responses.map((response) => response.json),
        }))
      case UPDATE_MANY:
        return Promise.all(
          params.ids.map((id: Identifier) =>
            httpClient(`${apiUrl}/${resource}/${id}`, {
              method: 'PUT',
              body: JSON.stringify(params.data),
            })
          )
        ).then((responses) => ({
          data: responses.map((response) => response.json),
        }))
      case DELETE_MANY:
        return Promise.all(
          params.ids.map((id: Identifier) =>
            httpClient(`${apiUrl}/${resource}/${id}`, {
              method: 'DELETE',
            })
          )
        ).then((responses) => ({
          data: responses.map((response) => response.json),
        }))
      default:
        break
    }

    const { url, options } = convertDataRequestToHttp(type, resource, params)
    return httpClient(url, options).then((response) => convertHttpResponse(response, type, params))
  }
}

export default drfProvider
